/**
 * colorGrade — a screen-space colour-grade: a full-viewport vertical wash that
 * unifies a scene's palette (the final cinematic grade over everything below it).
 * A FRAME kind (screen-fixed, mooded:false) at Z_LAYERS.COLOR_GRADE — above the
 * vignette (so it grades the whole scene incl. the vignette) but below ability
 * VFX/HUD (1000) so spell VFX + the HUD read punchy through it.
 *
 * Built from the same parts as `skyGradient`: one GPU-interpolated vertical fill,
 * drawn ONCE into the M1 per-layer container, then re-centered on the live camera each frame (vignette's camera-fit)
 * so the world-space graphic reads as screen-fixed. The fill's `strength` alpha
 * lets the scene show through (a grade, not a repaint). A flat single-colour grade
 * is the degenerate `top === bottom` case.
 *
 * No 'multiply' exists in VfxBlendMode, so: 'screen' (default) LIFTS shadows toward
 * the ramp (cinematic), 'normal' is a flat veil + the only darken path, 'add'
 * brightens. Static → reduce-motion N/A (only camera-fit bookkeeping, ungated like
 * vignette/skyGradient). Opacity is the container's concern (layer.opacity) — the
 * builder never touches container.alpha; strength flows only through the fill alpha.
 */
import { registerEffect, makeContentHandle, type EffectBuilder } from '../effectBuilders';
import { createGraphics } from '../../../rendering/utils/Graphics';
import { Z_LAYERS } from '../../../rendering/Z_LAYERS';
import { gradientColors } from './skyGradientConfig';

export const COLOR_GRADE_PAD = 2.5;

const buildColorGrade: EffectBuilder = (layer, ctx) => {
  if (layer.kind !== 'colorGrade') return null;

  const gfx = createGraphics(null, { depth: Z_LAYERS.COLOR_GRADE, parent: ctx.layerContainer });
  gfx.setBlendMode(layer.blend ?? 'screen');
  const bands = layer.bands ?? 24;

  // Mutable grade state — `setGrade` (the TimeOfDayDriver phase-2 palette push)
  // updates these + flags a redraw; the camera-fit tick rebuilds it on the next
  // frame. Initial values are the authored layer's own grade.
  let curTop = layer.top;
  let curBottom = layer.bottom;
  let curStrength = layer.strength;
  let needsRedraw = true;

  // The camera may NOT be bound yet at mount (LEVEL_LOADED can win the race vs the
  // player spawn). So do NOT gate the build on the camera — build the graphic now
  // (so the layer + its container exist) and draw the gradient on the FIRST tick
  // the camera is available, then keep it screen-fixed each frame. Draw ONCE (no
  // zoom → the viewport's world W/H is constant); the fill's `strength` alpha
  // lets the scene show through. Oversize by `pad` so a fast pan never reveals an
  // un-graded edge. Redraw ALSO when `setGrade` changed the palette (phase 2).
  let drawnW = 0;
  let drawnH = 0;
  const fit = () => {
    const v = ctx.getCameraView();
    if (!v) return; // camera not bound yet — try again next tick (no draw, no pop)
    const pad = COLOR_GRADE_PAD;
    const W = v.width * pad;
    const H = v.height * pad;
    // (Re)draw the gradient on the FIRST camera tick, whenever the viewport
    // size changes (window resize), OR when setGrade flagged a palette change —
    // else a maximise past the pad would reveal an un-graded edge (vignette
    // re-sizes each frame; we redraw only on change since size is stable during
    // play — no zoom — so this is rare, not per-frame).
    if (needsRedraw || W !== drawnW || H !== drawnH) {
      gfx.clear();
      if (gfx.fillVerticalGradientRect) {
        // A single interpolated fill has no full-width strip boundaries for
        // the sky grade to compound into visible horizontal scanlines.
        gfx.fillVerticalGradientRect(0, 0, W, H, curTop, curBottom, curStrength);
      } else {
        // Lightweight renderer/test fallback.
        const colors = gradientColors(curTop, curBottom, bands);
        const strip = H / colors.length;
        for (let i = 0; i < colors.length; i += 1) {
          const y0 = Math.round(i * strip);
          const h = Math.round((i + 1) * strip) - y0;
          gfx.fillRect(0, y0, W, h, colors[i], curStrength);
        }
      }
      drawnW = W;
      drawnH = H;
      needsRedraw = false;
    }
    // Screen-fix: the M1 container inherits the camera transform, so a static rect
    // would scroll off with the world — re-center on the live camera each frame.
    gfx.setPosition(v.centerX - W / 2, v.centerY - H / 2);
  };
  fit();
  const unsub = ctx.addTick(fit);

  return makeContentHandle({
    onDestroy: () => { unsub(); try { gfx.destroy(); } catch { /* gone */ } },
    setGrade: (top, bottom, strength) => {
      curTop = top;
      curBottom = bottom;
      curStrength = strength;
      needsRedraw = true; // rebuild on the next camera-fit tick
    },
  });
};

registerEffect({ kind: 'colorGrade', build: buildColorGrade, depth: Z_LAYERS.COLOR_GRADE, mooded: false });
